import { useLabels } from 'labels/hooks/useLabels/useLabels'
import type { Store } from 'maps/types'
import { cx } from 'utils/cx'

import { Collections } from './Collections'
import { Coordinates } from './Coordinates'
import { Sizes } from './Sizes'
import { StreetAddress } from './StreetAddress'

import text from 'fukku/styles/texts.module.scss'
import styles from './StoreAddress.module.scss'

const COLLECTIONS_TO_TRANSLATE = ['kids', 'men', 'women']

interface StoreAddressProps {
	readonly showSizes?: boolean
	readonly store: Store
	readonly close?: boolean
}

export function StoreAddress({
	showSizes,
	store,
	close = false,
}: StoreAddressProps): React.ReactNode {
	const { t } = useLabels()

	const { id, addresses, collections } = store
	const {
		shoppingCenter,
		city,
		postalCode,
		address,
		coordinates,
		streetAddress,
	} = addresses
	const collectionsTranslations = collections.map((collection) => {
		if (COLLECTIONS_TO_TRANSLATE.includes(collection.toLowerCase())) {
			return t(`storeLocator.card.line.${collection.toLowerCase()}.text`)
		}
		return collection
	})
	const hasCollections = collectionsTranslations.length > 0

	const sizes =
		showSizes && store.details?.sizesIds
			? store.details.sizesIds
					.map((size) => {
						return size.name
					})
					.join(', ')
			: undefined

	return (
		<div className={styles.description} id={`store-${id}-description`}>
			<div className={styles.address} data-testid='maps.address.store.info'>
				<div>
					{shoppingCenter && (
						<p className={cx(text.titleM, styles.noMargin)}>{shoppingCenter}</p>
					)}
					<p
						className={cx(!shoppingCenter && text.bodyM, styles.noMargin)}
						data-testid='maps.address.store.address'
					>
						{address}
					</p>
					<StreetAddress streetAddress={streetAddress} />
					<p
						className={cx(
							text.bodyM,
							styles.city,
							hasCollections && styles.withCollections
						)}
					>
						{city} {postalCode}
					</p>
					<Collections collections={collectionsTranslations} />
					<Sizes sizes={showSizes ? sizes : undefined} />
				</div>
				<Coordinates coordinates={coordinates} close={close} />
			</div>
		</div>
	)
}
